import React, { useRef, useState, useEffect } from 'react';
import SignatureCanvas from 'react-signature-canvas';
import './SignaturePadModal.css';

/**
 * Click-to-open signature field.
 *
 * Shows a slim preview box; clicking it opens a large signing pad in an
 * overlay (rendered above any parent modal) where the user can sign on a
 * comfortably sized canvas and save.
 *
 * Controlled component:
 * @param {string|null} value     - saved signature as a data URL (or null)
 * @param {function}    onChange  - called with the data URL on save, or null on clear
 * @param {string}      label     - field label
 * @param {boolean}     required  - show required asterisk
 * @param {string}      placeholder - prompt text shown in the empty preview
 */
const SignaturePadModal = ({
    value,
    onChange,
    label = 'Signature',
    required = false,
    placeholder = 'Tap here to sign'
}) => {
    const [open, setOpen] = useState(false);
    const [dims, setDims] = useState({ width: 600, height: 280 });
    const padRef = useRef(null);
    const bodyRef = useRef(null);

    // Size the canvas to the available width whenever the pad opens or resizes.
    useEffect(() => {
        if (!open) return;
        const measure = () => {
            if (!bodyRef.current) return;
            const width = bodyRef.current.clientWidth;
            const height = Math.max(220, Math.min(360, Math.round(width * 0.45)));
            setDims({ width, height });
        };
        const id = requestAnimationFrame(measure);
        window.addEventListener('resize', measure);
        return () => {
            cancelAnimationFrame(id);
            window.removeEventListener('resize', measure);
        };
    }, [open]);

    const closePad = () => setOpen(false);

    const handleSave = () => {
        if (padRef.current && !padRef.current.isEmpty()) {
            onChange(padRef.current.toDataURL());
            setOpen(false);
        }
    };

    const handleClearPad = () => {
        if (padRef.current) padRef.current.clear();
    };

    const handleRemove = (e) => {
        e.stopPropagation();
        onChange(null);
    };

    return (
        <div className="sig-field">
            {label && (
                <label className="sig-field-label">
                    {label} {required && <span className="sig-required">*</span>}
                </label>
            )}

            <div
                className={`sig-preview ${value ? 'has-signature' : ''}`}
                onClick={() => setOpen(true)}
                role="button"
                tabIndex={0}
                onKeyDown={(e) => {
                    if (e.key === 'Enter' || e.key === ' ') {
                        e.preventDefault();
                        setOpen(true);
                    }
                }}
            >
                {value ? (
                    <>
                        <img src={value} alt="Signature" className="sig-preview-img" />
                        <span className="sig-preview-hint">Tap to re-sign</span>
                        <button
                            type="button"
                            className="sig-preview-remove"
                            onClick={handleRemove}
                            title="Remove signature"
                        >
                            ×
                        </button>
                    </>
                ) : (
                    <span className="sig-preview-placeholder">✍️ {placeholder}</span>
                )}
            </div>

            {open && (
                <div className="sig-modal-overlay" onClick={closePad}>
                    <div className="sig-modal" onClick={(e) => e.stopPropagation()}>
                        <div className="sig-modal-header">
                            <span>{label}</span>
                            <button type="button" className="sig-modal-close" onClick={closePad}>
                                ×
                            </button>
                        </div>
                        <div className="sig-modal-body" ref={bodyRef}>
                            <SignatureCanvas
                                ref={padRef}
                                backgroundColor="white"
                                canvasProps={{
                                    width: dims.width,
                                    height: dims.height,
                                    className: 'sig-modal-canvas'
                                }}
                            />
                            <p className="sig-modal-instruction">
                                Sign above using your mouse, stylus or finger
                            </p>
                        </div>
                        <div className="sig-modal-footer">
                            <button type="button" className="btn btn-secondary" onClick={handleClearPad}>
                                Clear
                            </button>
                            <button type="button" className="btn btn-primary" onClick={handleSave}>
                                Save Signature
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SignaturePadModal;
